import { FC } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';

import styles from 'components/Header/MiddleBar/MainLogo/MiddleBarMainLogo.module.scss';
import {
  DataButton,
  DataLink,
  WebpImage,
} from 'components/Shared/SharedComponents';
import { MiddleBarMainLogoInterface } from 'components/Header/MiddleBar/MainLogo/declarations';
import { SVG_TYPE } from 'constants/constants';
import type { Link } from 'src/__generated__/graphqlTypes';

const checkIsHomepage = (path: string) => {
  const pathParts = path.split('/');
  const firstPart = pathParts[1]?.toLowerCase();
  const isHomepage = !firstPart || firstPart.includes('homepage') || firstPart === '#';

  return isHomepage;
};

const getImageProps = (logo: Link, isStickyCtaActivated: boolean) => {
  const { backgroundsCollection, image } = logo;
  let imageToUse = image;

  if (isStickyCtaActivated) {
    const [firstImage] = backgroundsCollection?.items || [];

    if (firstImage?.image) {
      imageToUse = firstImage.image!;
    }
  }

  if (!imageToUse) {
    return null;
  }

  const isSvg = imageToUse.contentType === SVG_TYPE;
  const title = imageToUse.title || 'Main logo';

  if (isSvg) {
    return {
      title,
      src: imageToUse.url!,
      image: 'external' as 'external',
    };
  }

  return {
    title,
    image: imageToUse,
  };
};

const MiddleBarMainLogo: FC<MiddleBarMainLogoInterface> = ({
  mainLogo,
  isMobileView,
  className,
  isStickyCtaActivated = false,
}) => {
  const { src } = mainLogo;
  const router = useRouter();

  const imageProps = getImageProps(mainLogo, isStickyCtaActivated);

  if (!imageProps) {
    return null;
  }

  const mobileClassPrefix = isMobileView ? 'Mobile' : '';

  const imageComponent = (
    <WebpImage
      image={imageProps.image}
      src={imageProps.src}
      title={imageProps.title}
      className={styles[`mainLogo${mobileClassPrefix}Img`]}
    />
  );

  const buttonClassName = cn(
    styles[`mainLogo${mobileClassPrefix}`],
    { [styles.withStickyCta]: isStickyCtaActivated },
    className,
  );

  if (checkIsHomepage(router.asPath)) {
    return (
      <DataButton
        link={mainLogo}
        className={buttonClassName}
      >
        {imageComponent}
      </DataButton>
    );
  }

  return (
    <DataLink
      link={mainLogo}
      href={src!}
      className={buttonClassName}
    >
      {imageComponent}
    </DataLink>
  );
};

export default MiddleBarMainLogo;
